import { ReportCardDetailsProps } from "@carfax/web-ui/components/ReportCard/ReportCard";
import { expiryDateUtil } from "domain/User/Common/utils/expiryDateUtil";
import { VhrStatus } from "domain/User/Common/utils/dashboard-api";
import { IntlTranslationFunction } from "i18n/types";

export const calculateDetailsBasedOnDatesAndStatus = (
  purchaseDate: Date,
  expiryDate: Date,
  status: VhrStatus,
  expired: boolean,
  t: IntlTranslationFunction,
): ReportCardDetailsProps => {
  const daysToExpiration = expiryDateUtil.getDaysToExpiration(expiryDate);
  const displayedPurchaseDate = expiryDateUtil.generateFormattedDateString(purchaseDate);
  const displayedExpirationDate = expiryDateUtil.generateFormattedDateString(expiryDate);
  const reportExpirationString = generateReportExpirationString(daysToExpiration);

  function generateReportExpirationString(daysToExpiration: number): string {
    switch (daysToExpiration) {
      case 0:
        return t("report.reportExpirationString.today");
      case 1:
        return t("report.reportExpirationString.tomorrow");
      default:
        return t("report.reportExpirationString.default", { daysToExpiration });
    }
  }

  function isRevoked() {
    return status && status.valueOf() === VhrStatus.REVOKED.valueOf();
  }

  function isRefunded() {
    return status && status.valueOf() === VhrStatus.REFUNDED.valueOf();
  }

  function getActiveReportDetails(): ReportCardDetailsProps {
    return {
      purchaseDate: displayedPurchaseDate,
      expiryDate: displayedExpirationDate,
      expirationReason: reportExpirationString,
      testId: "report-card-active",
      expired: expired,
    };
  }

  function getExpiredReportDetails(): ReportCardDetailsProps {
    return {
      purchaseDate: displayedPurchaseDate,
      expiryDate: displayedExpirationDate,
      expirationReason: t("report.status.expired"),
      testId: "report-card-expired",
      expired: expired,
    };
  }

  function getRefundedReportDetails(): ReportCardDetailsProps {
    return {
      purchaseDate: displayedPurchaseDate,
      expiryDate: displayedExpirationDate,
      expirationReason: t("report.status.refunded"),
      testId: "report-card-refunded",
      expired: expired,
    };
  }

  function getRevokedReportDetails(): ReportCardDetailsProps {
    return {
      purchaseDate: displayedPurchaseDate,
      expiryDate: displayedExpirationDate,
      expirationReason: t("report.status.revoked"),
      testId: "report-card-revoked",
      expired: expired,
    };
  }

  function getExpirationDetails(): ReportCardDetailsProps {
    if (isRevoked()) {
      return getRevokedReportDetails();
    } else if (isRefunded()) {
      return getRefundedReportDetails();
    } else {
      return getExpiredReportDetails();
    }
  }

  return expired ? getExpirationDetails() : getActiveReportDetails();
};

export const detailsResolver = { calculateDetailsBasedOnExpiryAndStatus: calculateDetailsBasedOnDatesAndStatus };
